/**
 * Copilot Product Listener — the "send this to the Ask Redline team?" card.
 *
 * Copilot proposes the request when it hits a capability the product doesn't have; the manager
 * edits the one-line summary, optionally records a note, and presses Send. Nothing is stored
 * before that press, and the outcome is written onto the message so a refresh shows "Sent".
 */
import { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Lightbulb, Loader2, Send, Check, X, AlertTriangle } from "lucide-react";
import { SignalRecorder } from "./SignalRecorder";

export const CopilotSignalCard = ({ tenantId, signal, result, dismissed, onPatch, threadTitle }) => {
  const [summary, setSummary] = useState(signal.summary || "");
  const [recording, setRecording] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    if (!summary.trim()) { setError("Give the request a one-line summary first."); return; }
    setSending(true); setError("");
    try {
      const r = await api.post(`/tenants/${tenantId}/copilot/product-signals`, {
        verbatim: signal.verbatim, summary: summary.trim(), theme: signal.theme, source: "copilot_gap",
        thread_title: threadTitle || "", recording_id: recording?.id || null,
      });
      onPatch({ signal_result: { ...r.data.signal, message: r.data.message, at: new Date().toISOString() } });
      toast.success(r.data.message);
    } catch (e) {
      setError(errMsg(e, "That didn't send."));
    } finally {
      setSending(false);
    }
  };

  if (dismissed) {
    return (
      <div className="mt-2 text-[11px] text-ink/40 flex items-center gap-1" data-testid="copilot-signal-dismissed">
        <X className="h-3 w-3" /> Not sent.
      </div>
    );
  }
  if (result) {
    return (
      <div className="mt-2 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-3.5 py-2.5" data-testid="copilot-signal-sent">
        <div className="flex items-start gap-2">
          <Check className="h-4 w-4 text-emerald-700 shrink-0 mt-0.5" strokeWidth={2.5} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-ink">Sent to the Ask Redline team</div>
            <div className="text-xs text-ink/65 mt-0.5">“{result.summary}”</div>
            {result.recording && <div className="text-[11px] text-ink/50 mt-0.5">With your voice note.</div>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50/60 px-3.5 py-3 space-y-2.5" data-testid="copilot-signal-card">
      <div className="flex items-center gap-2">
        <Lightbulb className="h-3.5 w-3.5 text-amber-600 shrink-0" strokeWidth={2.5} />
        <div className="text-sm font-semibold text-ink min-w-0" data-testid="copilot-signal-title">
          Want this in Ask Redline? Send it to the team.
        </div>
      </div>
      <p className="text-[11px] text-ink/55 italic line-clamp-3" data-testid="copilot-signal-verbatim">You asked: “{signal.verbatim}”</p>
      <div>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-ink/50">In one line</label>
        <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} disabled={sending}
          data-testid="copilot-signal-summary" maxLength={300}
          className="mt-1 w-full resize-none rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm outline-none focus:border-ink disabled:opacity-60" />
      </div>
      <SignalRecorder tenantId={tenantId} value={recording} onChange={setRecording} disabled={sending} />
      {error && (
        <div className="flex items-start gap-1.5 text-[11px] text-rose-600" data-testid="copilot-signal-error">
          <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" strokeWidth={2.5} /> {error}
        </div>
      )}
      <div className="flex items-center gap-2 pt-0.5">
        <button onClick={send} disabled={sending} data-testid="copilot-signal-send"
          className="rounded-full bg-ink text-white text-xs font-semibold px-4 py-2 hover:bg-ink-700 transition-colors disabled:opacity-50 flex items-center gap-1.5">
          {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" strokeWidth={2.5} />}
          {sending ? "Sending…" : "Send to the team"}
        </button>
        <button onClick={() => onPatch({ signal_dismissed: true })} disabled={sending} data-testid="copilot-signal-dismiss"
          className="rounded-full border border-sand-200 bg-white text-xs font-semibold px-3.5 py-2 text-ink/70 hover:bg-sand-50 transition-colors disabled:opacity-50">
          Not now
        </button>
      </div>
    </div>
  );
};
